<template>
  <div class="speech-container">
    <button
      class="voice-btn"
      :class="{ recording, loading }"
      @click="toggleRecord"
      :disabled="loading"
      type="button"
    >
      <Mic v-if="!recording && !loading" class="icon mic" />
      <Square v-if="recording" class="icon stop" />
      <Loader2 v-if="loading" class="icon spinner" />
      
      <div v-if="recording" class="wave-overlay">
        <span></span><span></span><span></span><span></span>
      </div>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, onUnmounted } from 'vue'
import { Mic, Square, Loader2 } from 'lucide-vue-next'

const emit = defineEmits<{
  (e: 'recognized', text: string): void
}>()

const recording = ref(false)
const loading = ref(false)

// 使用引用类型保存流，确保在 stopRecord 中能访问到
let mediaStream: MediaStream | null = null
let mediaRecorder: MediaRecorder | null = null
let chunks: BlobPart[] = []

async function startRecord() {
  try {
    // 兼容性检查
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert('お使いのブラウザは音声入力をサポートしていないか、HTTPS接続ではありません。')
      return
    }

    // 将获取到的 stream 赋值给外部变量 mediaStream
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    mediaStream = stream 
    
    mediaRecorder = new MediaRecorder(stream)
    chunks = []

    mediaRecorder.ondataavailable = e => {
      if (e.data.size > 0) chunks.push(e.data)
    }
    
    mediaRecorder.onstop = uploadAudio

    mediaRecorder.start()
    recording.value = true
  } catch (err) {
    console.error('Microphone access denied or error:', err)
    alert('マイクのアクセスを許可してください。')
  }
}

function stopRecord() {
  //停止录制器
  if (mediaRecorder && mediaRecorder.state !== "inactive") {
    mediaRecorder.stop()
  }

  // 彻底关闭麦克风占用（解决移动端录制图标不消失的问题）
  if (mediaStream) {
    mediaStream.getTracks().forEach(track => {
      track.stop() // 必须显式停止音轨
    })
    mediaStream = null
  }

  recording.value = false // 立即切换状态，提升UI响应感
}

async function uploadAudio() {
  if (chunks.length === 0) return
  
  loading.value = true
  const blob = new Blob(chunks, { type: 'audio/webm' })
  const formData = new FormData()
  formData.append('file', blob, 'speech.webm')

  try {
    const res = await fetch('/api/util/speech_recognition', {
      method: 'POST',
      body: formData
    })
    if (!res.ok) throw new Error('Network response was not ok')
    const data = await res.json()
    if (data.text) {
      emit('recognized', data.text)
    }
  } catch (e) {
    console.error('Speech API error', e)
  } finally {
    loading.value = false
  }
}

function toggleRecord() {
  if (loading.value) return
  // 切换逻辑
  if (recording.value) {
    stopRecord()
  } else {
    startRecord()
  }
}

// 组件卸载时强制关闭麦克风，防止进程残留
onUnmounted(() => {
  stopRecord()
})
</script>

<style scoped>
.speech-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  position: relative;
}

.voice-btn {
  width: 44px; /* 移动端标准触控尺寸 */
  height: 44px;
  border-radius: 50%;
  border: none;
  background: radial-gradient(circle at 30% 30%, #1f2937, #0b0f1a);
  box-shadow: 0 0 10px rgba(0, 153, 255, 0.15), inset 0 0 8px rgba(255,255,255,0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all .2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  -webkit-tap-highlight-color: transparent;
  appearance: none;
}

/* 录制状态：兰博基尼红 */
.voice-btn.recording {
  background: radial-gradient(circle, #ff3b3b, #7a0000);
  box-shadow: 0 0 20px rgba(255, 59, 59, 0.4);
}

/* 加载状态：科技蓝 */
.voice-btn.loading {
  background: radial-gradient(circle, #1f4fff, #091f4d);
}

/* 图标优化 */
.icon {
  width: 20px;
  height: 20px;
  stroke-width: 2;
  z-index: 2;
}

.mic {
  color: #9aa4b2;
  filter: drop-shadow(0 0 4px rgba(0, 198, 255, 0.6));
}

.spinner {
  color: #4fc3f7;
  animation: spin 1s linear infinite;
}

/* 波纹覆盖层：移入按钮内部，更精致 */
.wave-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  pointer-events: none;
}

.wave-overlay span {
  width: 2px;
  height: 4px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 1px;
  animation: wave 1s infinite ease-in-out;
}

.wave-overlay span:nth-child(2) { animation-delay: 0.1s; }
.wave-overlay span:nth-child(3) { animation-delay: 0.2s; }
.wave-overlay span:nth-child(4) { animation-delay: 0.3s; }

/* 动画 */
@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes wave {
  0%, 100% { height: 4px; opacity: 0.3; }
  50% { height: 16px; opacity: 1; }
}

/* 移动端反馈 */
@media (max-width: 768px) {
  .voice-btn:active {
    transform: scale(0.92);
    filter: brightness(1.2);
  }
}
</style>